import { AuthConfig } from 'angular-oauth2-oidc';
import { environment as env } from './environment';
import { InjectionToken } from '@angular/core';
import { Backend } from '@telus/d2c-core';

export interface IEnvironment {
  environment: string;
  appName: string;
  appDomain: string;
  appTeamDomain: string;
  ptnrDomain: string;
  redirectUri: string;
  kongLogoutURL: string;
  kongLogoutTeamURL: string;
  production: boolean;
  oauth: {
    vendor: AuthConfig;
    team: AuthConfig;
    headers: {
      vendor: {
        [name: string]: string;
      };
    };
  };
  k2View: {
    [itemsPerPage: string]: number;
  };
  domains: { [key: string]: string };
  api: {
    backend: Backend;
    headers: {
      central: {
        [name: string]: string;
      };
      k2view: {
        [name: string]: string;
      };
      d2cGeo: {
        [name: string]: string;
      };
      ams: {
        [name: string]: string;
      };
      dsa: {
        [name: string]: string;
      };
    };
  };
  baseUrl: string;
  app: {
    systemConfig: {
      provinces: 'Provinces';
    };
    address360: {
      limit: number;
    };
    fsa: {
      limit: number;
    };
    user: {
      itemsPerRequest: number;
    };
    role: {
      itemsPerRequest: number;
    };
    privilege: {
      itemsPerRequest: number;
    };
    sort: {
      addressWalklist: any;
    };
    columns: {
      filters: {
        group: any;
        salesrep: any;
        d2cgeoserviceaddress: any;
      };
      sorting: {
        group: any;
        salesrep: any;
        d2cgeoserviceaddress: any;
      };
    };
    pageSize: number;
    page: number;
    tableMaxColumns: number;
    breakPointTablet: number;
    breakPointDesktop: number;
    defaultAddressFormat: string;
    defaultDateFilterFormat: string;
  };
}

export const environment: IEnvironment = {
  environment: env.environment,
  appName: env.appName,
  appDomain: env.appDomain,
  appTeamDomain: env.appTeamDomain,
  ptnrDomain: env.ptnrDomain,
  redirectUri: env.redirectUri,
  kongLogoutURL: env.kongLogoutURL,
  kongLogoutTeamURL: env.kongLogoutTeamURL,
  production: env.production,
  oauth: env.oauth,
  domains: env.domains,
  k2View: env.k2view,
  api: {
    backend: {
      central: {
        desc: false,
        endpoint: {
          territory: env.endpoints.territoryManagement,
          appConfig: env.endpoints.appConfig,
          iam: env.endpoints.iamserver,
          activity: env.endpoints.activity,
        },
      },
      d2cGeo: {
        endpoint: env.endpoints.d2cGeo,
      },
      reproster: {
        endpoint: env.endpoints.reproster,
      },
      geoserver: {
        endpoint: env.endpoints.geoserver,
      },
      k2view: {
        endpoint: env.endpoints.k2view,
      },
      ams: {
        endpoint: env.endpoints.ams,
      },
      dsa: {
        endpoint: env.endpoints.dsa,
      },
    },
    headers: env.headers,
  },
  baseUrl: env.baseUrl,
  app: {
    systemConfig: {
      provinces: 'Provinces',
    },
    // this is for paging
    address360: {
      limit: 200,
    },
    fsa: {
      limit: 5000,
    },
    user: {
      itemsPerRequest: 1000,
    },
    role: {
      itemsPerRequest: 1000,
    },
    privilege: {
      itemsPerRequest: 1000,
    },
    pageSize: 100,
    page: 0,
    tableMaxColumns: 5,
    breakPointTablet: 0,
    breakPointDesktop: 1024,
    defaultAddressFormat: 'MMM D, YYYY hh:mm A',
    defaultDateFilterFormat: 'YYYYMMDD',
    sort: {
      // Only sort by City, Street Name & Type (i.e. Street_Type_Prefix ||
      // Street_Name || Street_Type_Suffix), Street# (i.e. Street_number) and Unit#
      addressWalklist: [
        'city+A',
        'street_type_prefix+A',
        'street_name+A',
        'street_type_suffix+A',
        'street_number+A',
        'unit+A',
      ],
    },
    columns: {
      filters: {
        //Names should be column names
        //Vendor Group, Vendor Account, Created By, Last Modified By
        group: ['vendorName', 'vendorAccountName', 'createdBy', 'modifiedBy'],
        //Vendor Account, Active
        salesrep: ['vendorAccountName', 'isActive'],
        d2cgeoserviceaddress: [
          'unitNumber',
          'streetNumber',
          'streetName',
          'postalCode',
          'sayb',
          'walklistAssigned',
          'dnk',
          'city',
          'premiseType',
          'fsa',
          'dpStatus',
        ],
      },
      sorting: {
        group: undefined,
        //Vendor Sales Rep, Vendor Group, Vendor Account, Active, Store Code
        salesrep: [
          'name',
          'vendorGroupName',
          'vendorAccountName',
          'isActive',
          'storeCode',
        ],
        d2cgeoserviceaddress: [
          'unitNumber',
          'streetNumber',
          'streetName',
          'postalCode',
          'sayb',
          'walklistAssigned',
          'dnk',
          'city',
          'premiseType',
          'fsa',
          'dpStatus',
        ],
      },
    },
  },
};

export const ENV = new InjectionToken<IEnvironment>('telus.config');
